import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import {
  EQUIPMENT_SERIALIZED_TYPES,
  LOOKUP_META,
  RECORD_STATUSES,
  equipmentInputSchema,
  lookupInputSchema,
} from '@app/shared';
import type { EquipmentDto, LookupDto, Paged, RecordStatus } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { FormDialog } from '../../components/dialogs';
import {
  MoneyInput,
  NumberInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { api } from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import {
  useCreateLookup,
  useLookupOptions,
  useLookups,
  useUpdateLookup,
} from '../configuration/api';
import { useFeatureReleased } from '../../release';
import { useProductModelOptions } from './api';
import {
  CATALOGUE_IMAGES_FEATURE,
  ImageSlots,
  Thumbnail,
  equipmentImagePath,
  imageUrl,
  saveImageDraft,
} from './CatalogueImages';
import type { ImageDraft } from './CatalogueImages';
import { withRequiredMessages } from './formUtils';

type MasterKind = 'equipmentType' | 'equipmentStatusName';

/** What differs between the two name lists. */
const MASTER: Record<MasterKind, { nameLabel: string; addLabel: string; dateFilter: boolean }> = {
  // Reference filters: name, Status, date range.
  equipmentType: { nameLabel: 'Equipment Type', addLabel: 'Add Equipment Type', dateFilter: true },
  // Reference columns: #, Reason, Status, Created Date, Created By, Updated Date, Updated By, Action.
  equipmentStatusName: {
    nameLabel: 'Reason',
    addLabel: 'Add Equipment Status Name',
    dateFilter: false,
  },
};

const statusFilter = (value: string, onChange: (v: string) => void) => (
  <TextField select label="Status" value={value} onChange={(e) => onChange(e.target.value)}>
    <MenuItem value="">All</MenuItem>
    {RECORD_STATUSES.map((s) => (
      <MenuItem key={s} value={s}>
        {s}
      </MenuItem>
    ))}
  </TextField>
);

const auditColumns = <
  T extends {
    createdAt: string;
    createdBy: { loginId: string } | null;
    updatedAt: string;
    updatedBy: { loginId: string } | null;
  },
>(): Column<T>[] => [
  {
    key: 'createdAt',
    header: 'Created Date',
    sortable: true,
    nowrap: true,
    render: (r) => formatDate(r.createdAt),
  },
  { key: 'createdBy', header: 'Created By', render: (r) => r.createdBy?.loginId ?? '' },
  {
    key: 'updatedAt',
    header: 'Updated Date',
    sortable: true,
    nowrap: true,
    render: (r) => formatDate(r.updatedAt),
  },
  { key: 'updatedBy', header: 'Updated By', render: (r) => r.updatedBy?.loginId ?? '' },
];

interface NameForm {
  name: string;
  status: RecordStatus;
}

function NameDialog(props: {
  kind: MasterKind;
  open: boolean;
  entry?: LookupDto;
  onClose: () => void;
}) {
  const { kind, open, entry, onClose } = props;
  const meta = MASTER[kind];
  const notify = useNotify();
  const create = useCreateLookup(kind);
  const update = useUpdateLookup(kind);
  const { control, handleSubmit, reset, setError } = useForm<NameForm>({
    defaultValues: { name: '', status: 'Active' },
    resolver: withRequiredMessages<NameForm>(
      zodResolver(lookupInputSchema.pick({ name: true, status: true })) as Resolver<NameForm>,
      { name: `${meta.nameLabel} is required` },
    ),
  });
  useEffect(() => {
    if (open)
      reset(entry ? { name: entry.name, status: entry.status } : { name: '', status: 'Active' });
  }, [open, entry, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      if (entry) await update.mutateAsync({ id: entry.id, input: values });
      else await create.mutateAsync(values);
      notify.success(`${meta.nameLabel} ${entry ? 'updated' : 'added'}.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={entry ? `Update ${LOOKUP_META[kind].itemLabel}` : meta.addLabel}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={create.isPending || update.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput
          control={control}
          name="name"
          label={meta.nameLabel}
          required
          autoFocus
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
      </Box>
    </FormDialog>
  );
}

/**
 * Manage Equipment Type (PC683) and Manage Equipment Status Name (PC727): two lists of names
 * with a status, kept in the managed-list store.
 */
export function EquipmentNameListPage({ kind }: { kind: MasterKind }) {
  const meta = MASTER[kind];
  const list = useListState({ q: '', status: '', createdFrom: '', createdTo: '' }, 10, 'name');
  const entries = useLookups(kind, list.params);
  const [form, setForm] = useState<{ entry?: LookupDto } | null>(null);

  const columns: Column<LookupDto>[] = [
    { key: '#', header: '#', width: 50, render: (_r, i) => rowNumber(i, list.page, list.pageSize) },
    { key: 'name', header: meta.nameLabel, sortable: true, render: (r) => r.name },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusChip status={r.status} />,
    },
    ...auditColumns<LookupDto>(),
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            aria-label={`Edit ${r.name}`}
            onClick={() => setForm({ entry: r })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={LOOKUP_META[kind].title}
        code={LOOKUP_META[kind].code}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
            {meta.addLabel}
          </Button>
        }
      />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label={meta.nameLabel}
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        {statusFilter(list.draft.status, (v) => list.setField('status', v))}
        {meta.dateFilter
          ? (['createdFrom', 'createdTo'] as const).map((key) => (
              <TextField
                key={key}
                type="date"
                label={key === 'createdFrom' ? 'Created Date From' : 'Created Date To'}
                value={list.draft[key]}
                onChange={(e) => list.setField(key, e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            ))
          : null}
      </FilterPanel>
      <DataTable
        label={LOOKUP_META[kind].title}
        columns={columns}
        rows={entries.data?.data}
        rowKey={(r) => r.id}
        loading={entries.isFetching}
        error={entries.error}
        onRetry={() => void entries.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={entries.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />
      <NameDialog
        kind={kind}
        open={Boolean(form)}
        entry={form?.entry}
        onClose={() => setForm(null)}
      />
    </Box>
  );
}

/* ------------------------------ Manage Equipment --------------------------- */

interface EquipmentForm {
  name: string;
  typeId: string;
  modelId: string | null;
  serializedType: (typeof EQUIPMENT_SERIALIZED_TYPES)[number];
  priceCents: number | undefined;
  inStock: number | null;
  status: RecordStatus;
}
const EMPTY: EquipmentForm = {
  name: '',
  typeId: '',
  modelId: null,
  serializedType: 'Nonserialized',
  priceCents: 0,
  inStock: 0,
  status: 'Active',
};

function EquipmentDialog(props: { open: boolean; item?: EquipmentDto; onClose: () => void }) {
  const { open, item, onClose } = props;
  const notify = useNotify();
  const queryClient = useQueryClient();
  const types = useLookupOptions('equipmentType', open);
  const models = useProductModelOptions(open);
  const { control, handleSubmit, reset, setError } = useForm<EquipmentForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<EquipmentForm>(
      zodResolver(equipmentInputSchema) as unknown as Resolver<EquipmentForm>,
      { typeId: 'Select Equipment Type' },
    ),
  });
  useEffect(() => {
    if (!open) return;
    reset(
      item
        ? {
            name: item.name,
            typeId: item.type?.id ?? '',
            modelId: item.model?.id ?? null,
            serializedType: item.serializedType,
            priceCents: item.priceCents,
            inStock: item.inStock,
            status: item.status,
          }
        : EMPTY,
    );
  }, [open, item, reset]);

  const featureOn = useFeatureReleased();
  const [image, setImage] = useState<ImageDraft>({});
  useEffect(() => {
    if (open) setImage({});
  }, [open]);
  const save = useMutation({
    mutationFn: async (input: EquipmentForm) => {
      const saved = item
        ? await api.patch<EquipmentDto>(`/equipment/${item.id}`, input)
        : await api.post<EquipmentDto>('/equipment', input);
      // Image 1 goes once the equipment is saved, so a new one has its id.
      await saveImageDraft(image, () => equipmentImagePath(saved.id));
      return saved;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['equipment'] }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await save.mutateAsync({
        ...values,
        priceCents: values.priceCents ?? 0,
        inStock: values.inStock ?? 0,
      });
      notify.success(item ? 'Equipment updated.' : 'Equipment added.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={item ? 'Update Equipment' : 'Add Equipment'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput
          control={control}
          name="name"
          label="Equipment Name"
          required
          autoFocus
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <SelectInput
          control={control}
          name="typeId"
          label="Equipment Type"
          required
          emptyLabel="Select"
          options={(types.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />
        <SelectInput
          control={control}
          name="modelId"
          label="Model"
          emptyLabel="None"
          options={(models.data ?? []).map((m) => ({
            value: m.id,
            label: `${m.make} ${m.model}`,
          }))}
        />
        <SelectInput
          control={control}
          name="serializedType"
          label="Serialized Type"
          required
          options={toOptions(EQUIPMENT_SERIALIZED_TYPES)}
        />
        <MoneyInput control={control} name="priceCents" label="Equipment Amount($)" required />
        <NumberInput
          control={control}
          name="inStock"
          label="Equipment In Stock"
          required
          emptyValue={null}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          required
          options={toOptions(RECORD_STATUSES)}
        />
        {featureOn(CATALOGUE_IMAGES_FEATURE) ? (
          <ImageSlots
            slots={[
              {
                slot: 1,
                label: 'Image 1',
                url: item?.image ? imageUrl(equipmentImagePath(item.id), item.image) : null,
              },
            ]}
            draft={image}
            onChange={setImage}
          />
        ) : null}
      </Box>
    </FormDialog>
  );
}

/** Manage Equipment (PC679): the reference's "Equipment List". */
export function EquipmentPage() {
  const list = useListState({ q: '', typeId: '', status: '', modelId: '' }, 10, 'name');
  const equipment = useQuery({
    queryKey: ['equipment', 'list', list.params],
    queryFn: () => api.get<Paged<EquipmentDto>>('/equipment', list.params),
    placeholderData: (prev) => prev,
  });
  const types = useLookupOptions('equipmentType');
  const models = useProductModelOptions();
  const [form, setForm] = useState<{ item?: EquipmentDto } | null>(null);
  const featureOn = useFeatureReleased();

  const columns: Column<EquipmentDto>[] = [
    { key: '#', header: '#', width: 50, render: (_r, i) => rowNumber(i, list.page, list.pageSize) },
    { key: 'name', header: 'Equipment Name', sortable: true, render: (r) => r.name },
    { key: 'type', header: 'Equipment Type', render: (r) => r.type?.name ?? '' },
    { key: 'model', header: 'Model', render: (r) => r.model?.label ?? '' },
    ...(featureOn(CATALOGUE_IMAGES_FEATURE)
      ? [
          {
            key: 'image',
            header: 'Image',
            render: (r: EquipmentDto) =>
              r.image ? (
                <Thumbnail
                  src={imageUrl(equipmentImagePath(r.id), r.image)}
                  alt={`${r.name} image`}
                />
              ) : (
                ''
              ),
          },
        ]
      : []),
    {
      key: 'priceCents',
      header: 'Equipment Amount($)',
      sortable: true,
      align: 'right',
      render: (r) => formatCents(r.priceCents),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusChip status={r.status} />,
    },
    ...auditColumns<EquipmentDto>(),
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            aria-label={`Edit equipment ${r.name}`}
            onClick={() => setForm({ item: r })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Equipment List"
        code="PC679"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
            Add Equipment
          </Button>
        }
      />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Equipment Name"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        <TextField
          select
          label="Equipment Type"
          value={list.draft.typeId}
          onChange={(e) => list.setField('typeId', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(types.data ?? []).map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        {statusFilter(list.draft.status, (v) => list.setField('status', v))}
        <TextField
          select
          label="Model"
          value={list.draft.modelId}
          onChange={(e) => list.setField('modelId', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(models.data ?? []).map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.make} {m.model}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>
      <DataTable
        label="Equipment List"
        columns={columns}
        rows={equipment.data?.data}
        rowKey={(r) => r.id}
        loading={equipment.isFetching}
        error={equipment.error}
        onRetry={() => void equipment.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={equipment.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />
      <EquipmentDialog open={Boolean(form)} item={form?.item} onClose={() => setForm(null)} />
    </Box>
  );
}
